import React from 'react';
import styled from 'styled-components';
import {
  useAllProjectsTelStatusMonthlyReportsQuery,
  useTelStatusesByClientIdQuery,
} from 'api';
import useSearchParams from '../useSearchParams';
import moment from 'moment';
import {StatusEclipse} from 'components/Ui';
import telCategoryColor from 'helpers/telCategoryColor';
import _ from 'lodash';
import {Button} from 'components/antd';
import {ArrowRight, Download} from 'components/Ui/Icon';
import DownloadModal from './PreleadReportDownloadModal';
import {telStatusCategoryHash} from 'helpers/telStatusCategoryHash';

export default () => {
  const [isModalOpen, setModalOpen] = React.useState(false);
  const {query, searchParams, setQuery} = useSearchParams();
  const [displayCategories, setDisplayCategories] = React.useState([]);
  const {data: {allProjectsTelStatusMonthlyReports = []} = {}} =
    useAllProjectsTelStatusMonthlyReportsQuery({
      variables: {
        search: searchParams,
      },
      fetchPolicy: 'network-only',
    });

  const {data: {telStatusesByClientId = []} = {}} =
    useTelStatusesByClientIdQuery({
      variables: {
        search: searchParams,
      },
      fetchPolicy: 'network-only',
    });

  const categorizedTelStatuses = React.useCallback(
    (category: string) =>
      telStatusesByClientId.filter(
        (telStatus) => telStatus.category === category,
      ),
    [telStatusesByClientId],
  );

  const totalCount = (object: any): any => {
    const value = Object.values(object).reduce(
      (total: number, value: number) => total + value,
      0,
    );
    if (!value) return null;

    return value;
  };

  const toggle = React.useCallback(
    (category: string) => {
      if (displayCategories.includes(category)) {
        setDisplayCategories(
          displayCategories.filter(
            (displayCategory) => displayCategory != category,
          ),
        );
      } else {
        setDisplayCategories([...displayCategories, category]);
      }
    },
    [displayCategories],
  );

  if (allProjectsTelStatusMonthlyReports.length === 0)
    return (
      <>
        <DownloadModal isOpen={isModalOpen} setModal={setModalOpen} />
        <DownloadButton onClick={() => setModalOpen(true)}>
          <Download />
          ダウンロード
        </DownloadButton>
      </>
    );

  let monthlyCounts: {[key: string]: number} = {};
  allProjectsTelStatusMonthlyReports.forEach((report) => {
    const monthlyCount =
      totalCount(report.appoint) +
      totalCount(report.call) +
      totalCount(report.callNg) +
      totalCount(report.contact) +
      totalCount(report.contactNg) +
      totalCount(report.document) +
      totalCount(report.other) +
      totalCount(report.reception) +
      totalCount(report.receptionNg);
    monthlyCounts[report.countDate] = monthlyCount;
  });

  let telCategoryCounts: {[key: string]: number} = {};
  Object.keys(telStatusCategoryHash).forEach((category) => {
    telCategoryCounts[category] = allProjectsTelStatusMonthlyReports.reduce(
      (total: number, report: any) =>
        total + totalCount(report[_.camelCase(category)]),
      0,
    );
  });

  let telStatusCounts: {[key: string]: number} = {};
  Object.keys(telStatusCategoryHash).forEach((category) => {
    categorizedTelStatuses(category).forEach((telStatus) => {
      telStatusCounts[telStatus.id] = allProjectsTelStatusMonthlyReports.reduce(
        (total: number, report: any) =>
          report[category]?.[telStatus.id]
            ? total + report[category][telStatus.id]
            : total,
        0,
      );
    });
  });

  return (
    <Container>
      <div>
        <h2>ステータス・カテゴリ別</h2>
        <div>
          <Display>
            <span>
              {query.date
                ? moment(query.date).format('YYYY年')
                : moment().format('YYYY年')}
            </span>
            <Button
              type="text"
              className="left"
              onClick={() =>
                setQuery({
                  date: moment(query.date)
                    .subtract(1, 'years')
                    .format('YYYY-MM-DD'),
                })
              }>
              <ArrowRight />
            </Button>
            <Button
              type="text"
              onClick={() =>
                setQuery({
                  date: moment(query.date).add(1, 'years').format('YYYY-MM-DD'),
                })
              }>
              <ArrowRight />
            </Button>

            <Button
              className="today"
              onClick={() => setQuery({date: moment().format('YYYY-MM-DD')})}>
              今年
            </Button>
          </Display>
          <div className="absolute right-0 top-[-50px]">
            <DownloadButton onClick={() => setModalOpen(true)}>
              <Download />
              ダウンロード
            </DownloadButton>
          </div>
        </div>
      </div>

      <Table>
        <div className="column far-left">
          <div className="far-top">
            <span>
              {query.date
                ? moment(query.date).format('YYYY年')
                : moment().format('YYYY年')}
            </span>
            <div className="all-count">
              {totalCount(monthlyCounts) ? totalCount(monthlyCounts) : 0}
            </div>
          </div>

          {Object.keys(telStatusCategoryHash).map((category) => (
            <React.Fragment key={category}>
              <div
                className={
                  'category ' + (!category.includes('ng') && 'openable')
                }
                onClick={() => toggle(category)}>
                {!category.includes('ng') && (
                  <Open
                    category={category}
                    displayCategories={displayCategories}>
                    <ArrowRight />
                  </Open>
                )}
                <StatusEclipse color={telCategoryColor(category)} />
                <span>{telStatusCategoryHash[category]}</span>
                <span className="category-count">
                  {telCategoryCounts[category]}
                </span>
              </div>
              {displayCategories.includes(category) &&
                !category.includes('ng') &&
                categorizedTelStatuses(category).map((telStatus) => (
                  <div key={telStatus.id}>
                    <span>{telStatus.name}</span>
                    <span className="category-count">
                      {telStatusCounts[telStatus.id]}
                    </span>
                  </div>
                ))}
            </React.Fragment>
          ))}
        </div>

        {allProjectsTelStatusMonthlyReports.map((report: any) => (
          <div className="column" key={report.countDate}>
            <div
              className={
                'far-top ' +
                (moment(report.countDate).isSame(new Date(), 'month') &&
                  'this-month')
              }>
              <span>{moment(report.countDate).format('M')}月</span>
              <div className="monthly-count">
                {monthlyCounts[report.countDate]}
              </div>
            </div>

            {Object.keys(telStatusCategoryHash).map((category) => (
              <React.Fragment key={category}>
                <div className="category">
                  <span>
                    {report[_.camelCase(category)] &&
                      totalCount(report[_.camelCase(category)])}
                  </span>
                </div>
                {displayCategories.includes(category) &&
                  !category.includes('ng') &&
                  categorizedTelStatuses(category).map((telStatus) => (
                    <div key={telStatus.id}>
                      <span>
                        {report[category] && report[category][telStatus.id]}
                      </span>
                    </div>
                  ))}
              </React.Fragment>
            ))}
          </div>
        ))}
      </Table>

      {isModalOpen && (
        <DownloadModal isOpen={isModalOpen} setModal={setModalOpen} />
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;

  > div:first-child {
    margin-bottom: 15px;
    display: flex;
    align-items: center;

    > h2 {
      margin: auto auto auto 0;
      font-weight: bold;
      font-size: 20px;
      line-height: 29px;
      color: var(--text-color-1);
    }

    > div {
      position: relative;
      margin: auto 0 auto auto;
      display: flex;
    }
  }
`;

const Display = styled.div`
  margin-left: 10px;

  > span {
    font-size: 12px;
    line-height: 17px;
    color: var(--text-color-1);
  }

  > button {
    padding: 5.6px 6px;

    svg {
      path {
        stroke: var(--text-color-2);
      }
    }

    &.left {
      svg {
        transform: rotate(180deg);
      }
    }

    &.today {
      margin-left: 10px;
      padding: 5.6px 10px;
      border: 1px solid #e1e6eb;
      box-sizing: border-box;
      border-radius: 4px;
    }
  }
`;

const DownloadButton = styled(Button)`
  display: flex;
  border: 1px solid #e1e6eb;
  border-radius: 4px;

  > svg {
    margin-right: 6px;
  }

  > span {
    margin: auto 0;
    font-weight: bold;
    font-size: 12px;
    color: var(--text-color-1);
  }
`;

const Table = styled.div`
  display: flex;
  border: 1px solid #e1e6eb;
  border-top: none;
  overflow: scroll;
  max-height: 600px;

  .column {
    display: flex;
    flex-direction: column;
    width: 100%;

    > div {
      width: 100%;
      min-height: 43px;
      border-top: 1px solid #e1e6eb;
      border-right: 1px solid #e1e6eb;
      display: flex;
      align-items: center;

      &:first-child {
        > span {
          margin: 0 auto;
        }

        &.far-top {
          top: 0;
          z-index: 3;
          position: sticky;
          background: #ffffff;
          border-bottom: 3px double #e1e6eb;
          display: flex;
          flex-direction: column;
          align-items: center;
          min-height: 94px;

          span {
            &:first-child {
              margin: 10px auto 0;
            }
          }

          &.this-month {
            border-top: 2px solid var(--text-color-1);

            span {
              color: var(--text-color-1);
              font-weight: bold;
            }
          }
        }
      }

      &:nth-child(2) {
        border-top: none;
      }

      > span {
        margin: 0 9px 0 auto;
        color: var(--text-color-2);
      }

      &.category {
        > span {
          color: var(--text-color-1);
          font-weight: bold;
        }
      }
    }

    &:last-child {
      > div {
        border-right: none;
      }
    }

    &.far-left {
      left: 0;
      z-index: 10;
      position: sticky;
      background: #ffffff;

      > div {
        width: 309px;
        display: flex;
        align-items: center;
        padding: 0 13px 0 40px;
        border-right: 3px double #e1e6eb;
        background: #fff;

        &.category {
          padding: 0 13px;

          svg {
            margin-right: 5px;
          }
          span {
            font-weight: bold;
          }
        }

        &.openable {
          cursor: pointer;
        }

        > span {
          margin: 0;
          font-size: 13px;
        }
      }
    }
    .all-count {
      padding-top: 26px;
      padding-bottom: 15px;
      margin: 0 0 0 auto;
      font-weight: bold;
    }
    .monthly-count {
      font-weight: bold;
      margin: 0 9px 0 auto;
      padding-top: 28px;
      padding-bottom: 15px;
    }
    .category-count {
      margin: 0 0 0 auto !important;
    }
  }
`;

const Open = styled.div<{category: string; displayCategories: any}>`
  display: flex;
  width: 13px;
  height: 13px;
  > svg {
    ${({category, displayCategories}) =>
      displayCategories.includes(category) && 'transform: rotate(90deg);'}

    width: 13px;
    height: 13px;
    fill: var(--text-color-3);
    path {
      width: 13px;
      height: 13px;
    }
  }
`;
